import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import { useQuery } from "@apollo/client";

import { FETCH_POLICY_CACHE_AND_NETWORK, I18N_NAMESPACES } from "../../../helpers/constants";
import { GET_WINE_NAME_AND_LARGE_PHOTO } from "../../../graphql/queries";
import AnimatingTrayContainer from "../../atoms/AnimatingTrayContainer";
import OffCanvasTray from "../OffCanvasTray";
import OffCanvasTrayHeader from "../../molecules/OffCanvasTrayHeader";
import OffCanvasTrayClose from "../../atoms/OffCanvasTrayClose";
import OffCanvasTrayHeaderTitle from "../../atoms/OffCanvasTrayHeaderTitle";
import OffCanvasTrayBody from "../../molecules/OffCanvasTrayBody";
import Title from "../../atoms/Title";
import { GetWinePhotos, GetWinePhotos_wine } from "../../../types/__generated__/GetWinePhotos";
import BuyAgainButtons from "../../molecules/BuyAgainButtons";
import SoldOutOptions from "../SoldOutOptions";
import RotatingBottleBox from "../../atoms/RotatingBottleBox";
import { CellarBottleHolder } from "../../atoms/CellarBottleHolder";
import { useAppContext } from "../Layout/context";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import URL_PATTERNS from "../../../urls";

interface BuyAgainTrayProps {
  isInBoxAndCartTray?: boolean;
  handleOffCanvasBuyAgainChange: () => void;
  wineSlug: string;
  baseColor: string;
  bottleImage: string;
}

const BuyAgainTray = ({
  isInBoxAndCartTray,
  handleOffCanvasBuyAgainChange,
  wineSlug,
  baseColor,
  bottleImage,
}: BuyAgainTrayProps) => {
  const [hide, setHide] = useState(false);
  const handleCloseTray = () => {
    setHide(true);
    setTimeout(() => handleOffCanvasBuyAgainChange(), 200);
  };

  const { memberId } = useAppContext();
  const { data, loading } = useQuery<GetWinePhotos>(GET_WINE_NAME_AND_LARGE_PHOTO, {
    partialRefetch: true,
    fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK,
    variables: {
      slug: wineSlug,
      memberId,
    },
  });

  const wine = data?.wine;

  return (
    <OffCanvasTray
      setOverflowHidden={false}
      isScrollLockEnabled
      isOffcanvasOpened
      outSideClickHandler={handleCloseTray}
      isInBoxAndCartTray={isInBoxAndCartTray}
    >
      <AnimatingTrayContainer isOpened hide={hide}>
        {loading ? (
          <StyledLoading />
        ) : (
          <BuyAgainTrayContent
            wine={wine}
            baseColor={baseColor}
            bottleImage={bottleImage}
            handleCloseTray={handleCloseTray}
            isInBoxAndCartTray={isInBoxAndCartTray}
          />
        )}
      </AnimatingTrayContainer>
    </OffCanvasTray>
  );
};

const BuyAgainTrayContent = ({
  wine,
  baseColor,
  bottleImage,
  handleCloseTray,
  isInBoxAndCartTray,
}: {
  wine: GetWinePhotos_wine | undefined | null;
  baseColor: string;
  bottleImage: string;
  handleCloseTray: () => void;
  isInBoxAndCartTray?: boolean;
}) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);

  if (!wine) {
    return <Redirect to={URL_PATTERNS.NOT_FOUND} />;
  }

  return wine?.hasStock ? (
    <>
      <OffCanvasTrayHeader>
        <OffCanvasTrayClose onClick={handleCloseTray} />
        <OffCanvasTrayHeaderTitle>{t("common:buyAgain")}</OffCanvasTrayHeaderTitle>
      </OffCanvasTrayHeader>

      <CellarBottleHolder baseColor={baseColor} smUpHidden>
        <RotatingBottleBox imgUrl={bottleImage} rotateMobile />
      </CellarBottleHolder>

      <OffCanvasTrayBody>
        <Title tag={2} uppercase hiddenSm>
          {t("common:buyAgain") ?? ""}
        </Title>
        <BuyAgainButtons
          isInBoxAndCartTray={isInBoxAndCartTray}
          wine={wine}
          baseColor={baseColor}
          bottleImage={bottleImage}
        />
      </OffCanvasTrayBody>
    </>
  ) : (
    <SoldOutOptions wine={wine} onCloseTray={handleCloseTray} />
  );
};

export default BuyAgainTray;
